type Point = [number, number, number];

/** Ambient particle orb from the AI Employee reference. Decorative only. */
export function startAmbientOrb(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return () => undefined;
  }

  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const points: Point[] = Array.from({ length: 1900 }, (_, i) => {
    const y = 1 - (2 * (i + 0.5)) / 1900;
    const radius = Math.sqrt(1 - y * y);
    const angle = i * Math.PI * (3 - Math.sqrt(5));
    return [Math.cos(angle) * radius, y, Math.sin(angle) * radius];
  });

  let width = 0;
  let height = 0;
  let frameId = 0;
  let last = 0;
  let time = 0;

  const draw = (t: number) => {
    const radius = Math.min(width * 0.35, height * 0.36);
    if (width < 1 || height < 1 || radius <= 0) {
      return;
    }

    const cx = width / 2;
    const cy = height / 2;
    ctx.clearRect(0, 0, width, height);

    const auraRadius = Math.max(Math.min(width, height) / 2 - 1, 1);
    const auraInner = Math.min(radius * 0.9, Math.max(auraRadius - 2, 0));
    const aura = ctx.createRadialGradient(cx, cy, auraInner, cx, cy, auraRadius);
    aura.addColorStop(0, 'rgba(8,124,255,0.55)');
    aura.addColorStop(0.42, 'rgba(70,158,255,0.32)');
    aura.addColorStop(0.75, 'rgba(140,190,255,0.12)');
    aura.addColorStop(1, 'rgba(186,216,255,0)');
    ctx.fillStyle = aura;
    ctx.beginPath();
    ctx.arc(cx, cy, auraRadius, 0, Math.PI * 2);
    ctx.fill();

    const core = ctx.createRadialGradient(
      cx - radius * 0.28,
      cy - radius * 0.3,
      Math.max(radius * 0.04, 0.01),
      cx,
      cy,
      radius * 1.04
    );
    const stops: Array<[number, string]> = [
      [0, 'rgba(143,211,255,.96)'],
      [0.38, 'rgba(62,158,255,.89)'],
      [0.76, 'rgba(17,116,237,.86)'],
      [0.93, 'rgba(15,101,218,.62)'],
      [1, 'rgba(8,124,255,0)'],
    ];
    stops.forEach(([offset, color]) => core.addColorStop(offset, color));
    ctx.fillStyle = core;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();

    const spin = t * 0.00011;
    const cosSpin = Math.cos(spin);
    const sinSpin = Math.sin(spin);
    const cosTilt = Math.cos(0.2);
    const sinTilt = Math.sin(0.2);

    points.forEach(([x, y, z]) => {
      const xx = x * cosSpin - z * sinSpin;
      const zz = x * sinSpin + z * cosSpin;
      const yy = y * cosTilt - zz * sinTilt;
      const zr = y * sinTilt + zz * cosTilt;
      const front = (zr + 1) / 2;
      const wave =
        1 +
        Math.sin(y * 8 + t * 0.0019 + z * 4) * 0.018 +
        Math.cos(x * 7 - t * 0.0011) * 0.012;
      const scale = 2.9 / (2.9 - zr * 0.28);
      ctx.fillStyle =
        front > 0.48
          ? `rgba(221,244,255,${0.25 + front * 0.67})`
          : `rgba(0,107,231,${0.2 + front * 0.3})`;
      ctx.beginPath();
      ctx.arc(
        cx + xx * radius * wave * scale,
        cy + yy * radius * wave * scale,
        0.4 + front * 0.55,
        0,
        Math.PI * 2
      );
      ctx.fill();
    });
  };

  const size = () => {
    const bounds = canvas.getBoundingClientRect();
    width = bounds.width;
    height = bounds.height;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    draw(time);
  };

  const frame = (now: number) => {
    if (now - last > 32) {
      if (!document.hidden) {
        time += motion.matches ? 0 : Math.min(now - (last || now), 80);
        draw(time);
      }
      last = now;
    }
    frameId = window.requestAnimationFrame(frame);
  };

  const observer = new ResizeObserver(size);
  observer.observe(canvas);
  frameId = window.requestAnimationFrame(frame);

  return () => {
    window.cancelAnimationFrame(frameId);
    observer.disconnect();
  };
}
